import { useState, useMemo, useCallback } from 'react'
import type { MenuItem, CustomizationItem } from '@restaurant/core'
import {
  buildSyntheticSizeGroup,
  getLowestVariantPrice,
  SYNTHETIC_SIZE_GROUP_ID,
  isSyntheticSizeOption,
  variantKeyFromOptionId,
} from '@restaurant/core'

export interface OptionGroupData {
  id: string
  title: string
  required: boolean
  maxSelections?: number
  options: Array<{
    id: string
    name: string
    price: number
    selected: boolean
    isRadio: boolean
  }>
}

export interface UseProductCustomizationReturn {
  selections: Record<string, Set<string>>
  specialInstructions: string
  optionGroups: OptionGroupData[]
  unitPrice: number
  selectedPriceVariant: string | undefined
  customizationsForCart: Array<{ customizationItemId: string; selectedOptionId: string }>
  onOptionChange: (groupId: string, optionId: string, selected: boolean, isRadio: boolean) => void
  setSpecialInstructions: (text: string) => void
  reset: (
    menuItem: MenuItem,
    initialSelections?: Record<string, Set<string>>,
    initialSpecialInstructions?: string,
  ) => void
}

export function useProductCustomization(
  initialMenuItem: MenuItem | null,
  initialSelections?: Record<string, Set<string>>,
  initialSpecialInstructions?: string,
): UseProductCustomizationReturn {
  const [menuItem, setMenuItem] = useState<MenuItem | null>(initialMenuItem)
  const [selections, setSelections] = useState<Record<string, Set<string>>>(
    initialSelections ?? {},
  )
  const [specialInstructions, setSpecialInstructions] = useState(
    initialSpecialInstructions ?? '',
  )

  const syntheticSizeGroup = useMemo(
    () => buildSyntheticSizeGroup(menuItem),
    [menuItem],
  )

  const allGroups: CustomizationItem[] = useMemo(() => [
    ...(syntheticSizeGroup ? [syntheticSizeGroup] : []),
    ...(menuItem?.customizations ?? []),
  ], [syntheticSizeGroup, menuItem])

  const optionGroups: OptionGroupData[] = useMemo(() =>
    allGroups.map((group) => {
      const isRadio = group.maxSelections === 1
      const groupSelections = selections[group.id] ?? new Set<string>()
      return {
        id: group.id,
        title: group.name,
        required: group.isRequired,
        maxSelections: group.maxSelections,
        options: group.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          price: opt.price,
          selected: groupSelections.has(opt.id),
          isRadio,
        })),
      }
    }),
  [allGroups, selections])

  const unitPrice = useMemo(() => {
    if (!menuItem) return 0
    const base = getLowestVariantPrice(menuItem)
    let customizationCost = 0
    for (const [groupId, optionIds] of Object.entries(selections)) {
      const group = allGroups.find((g) => g.id === groupId)
      if (!group) continue
      for (const optionId of optionIds) {
        const option = group.options.find((o) => o.id === optionId)
        if (option) customizationCost += option.price
      }
    }
    return base + customizationCost
  }, [menuItem, selections, allGroups])

  const selectedPriceVariant = useMemo(() => {
    const sizeSelections = selections[SYNTHETIC_SIZE_GROUP_ID]
    if (!sizeSelections?.size) return undefined
    const selectedOptionId = Array.from(sizeSelections)[0]
    return selectedOptionId ? variantKeyFromOptionId(selectedOptionId) : undefined
  }, [selections])

  const customizationsForCart = useMemo(() =>
    Object.entries(selections)
      .filter(([groupId]) => !isSyntheticSizeOption(groupId))
      .flatMap(([groupId, optionIds]) =>
        Array.from(optionIds).map((optionId) => ({
          customizationItemId: groupId,
          selectedOptionId: optionId,
        })),
      ),
  [selections])

  const onOptionChange = useCallback(
    (groupId: string, optionId: string, selected: boolean, isRadio: boolean) => {
      setSelections((prev) => {
        const updated = { ...prev }
        if (isRadio) {
          updated[groupId] = new Set([optionId])
        } else {
          const current = new Set(prev[groupId] ?? [])
          if (selected) {
            current.add(optionId)
          } else {
            current.delete(optionId)
          }
          updated[groupId] = current
        }
        return updated
      })
    },
    [],
  )

  const reset = useCallback(
    (
      newMenuItem: MenuItem,
      newSelections?: Record<string, Set<string>>,
      newSpecialInstructions?: string,
    ) => {
      setMenuItem(newMenuItem)
      setSelections(newSelections ?? {})
      setSpecialInstructions(newSpecialInstructions ?? '')
    },
    [],
  )

  return {
    selections,
    specialInstructions,
    optionGroups,
    unitPrice,
    selectedPriceVariant,
    customizationsForCart,
    onOptionChange,
    setSpecialInstructions,
    reset,
  }
}
